import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { UniversalSearchService } from '../../../../universal-search/services/universal-search.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CustomFiltersService } from '../../../../services/custom-filters.service';
import { SessionStorageService } from '../../../../services/session-storage.service';
import { Subject } from 'rxjs';
import { RightBridgeApiService } from '../../../../services/right-bridge-api.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-rep-code-filter',
  templateUrl: './repCode-filter.component.html',
  styleUrls: ['./repCode-filter.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    MatButtonModule,
  ],
})
export class RepCodeFilterComponent implements OnInit, OnDestroy {
  private ss = inject(SessionStorageService);
  usServ = inject(UniversalSearchService);
  cstmFltrSrvc = inject(CustomFiltersService);
  private rbs = inject(RightBridgeApiService);

  @Input() field;
  @Input() filterForm;
  @Input() groupKey;
  @Input() gridName: string;
  @Output() selectionChange = new EventEmitter();
  @Output() clearFilter = new EventEmitter();
  repCodeControl = new FormControl<any>('');
  unsubscribe = new Subject();
  dropdownLoading: boolean;
  globals = this.ss.get('globals');
  dropdownNoResults: boolean;
  dropdownRepCodes = [];
  previousSelectedVal = '';
  requestRepsSearch = false;
  repUnits: {
    repCode: string;
    repName: string;
    name?: string;
    unitId?: string;
    unitName?: string;
    unitType?: string;
  }[];

  ngOnInit(): void {
    this.globals = this.ss.get('globals');
    this.dropdownRepCodes = this.ss.get('repUnits');

    this.repCodeControl =
      this.filterForm.controls[this.groupKey].controls[this.field.varName];

    if (
      this.globals &&
      this.globals.user.managerUnitCount &&
      this.globals.user.managerUnitCount > 0
    ) {
      this.requestRepsSearch = true;
    } else {
      this.requestRepsSearch = false;
    }

    this.rbs.getRepCodeList().subscribe(res => {
      if (this.requestRepsSearch) {
        this.dropdownRepCodes = res;
      } else {
        this.repUnits = this.dropdownRepCodes.map(x => {
          return { repCode: x.repCode, repName: x.repName };
        });
        if (this.repUnits.length < 1) {
          this.repUnits = [];
        }
      }
    });

    this.repCodeControl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(value => {
        if (value && typeof value === 'string' && value.length > 0) {
          return this._filterReps(value);
        } else {
          if (this.requestRepsSearch) {
            this.repUnits = [];
          } else {
            this.repUnits = this.dropdownRepCodes;
          }
        }
      });

    this.cstmFltrSrvc.updateDropdowns$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: field => {
          if (field) {
            if (
              field['values']?.['field'] === this.field.varName &&
              (typeof field['values']?.['value'] === 'number' ||
                field['values']?.['value'])
            ) {
              this.repCodeControl.setValue(field['values']?.['value']);
              this.previousSelectedVal = field['values']?.['value'];
              this.filterForm.controls[this.groupKey].controls[
                this.field.varName
              ] = field['values']?.['value'];
            }

            if (
              this.field.conditionalField &&
              field['varName'] === this.field.conditionalField &&
              field?.['values']?.['value'] &&
              (field['values']['value'] === this.field.conditionalValue ||
                field['values']['value'].includes(this.field.conditionalValue))
            ) {
              this.repCodeControl.enable();
            }
          }
        },
        error: error => alert(error),
      });

    if (this.field.conditionalField) {
      this.cstmFltrSrvc.updateConditionalFilter$
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: passedFilter => {
            if (
              passedFilter['varName'] === this.field.conditionalField &&
              (passedFilter['values'] === this.field.conditionalValue ||
                passedFilter['values'].includes(this.field.conditionalValue))
            ) {
              this.repCodeControl.enable();
            } else if (
              passedFilter['varName'] === this.field.conditionalField &&
              (passedFilter['values'] !== this.field.conditionalValue ||
                !passedFilter['values'].includes(this.field.conditionalValue))
            ) {
              const resetEvent = new PointerEvent('pointerdown');
              this.resetFilter(resetEvent);
              this.repCodeControl.disable();
            }
          },
          error: error => console.error('Error: ', error),
        });
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  private _filterReps(value: string) {
    if (this.requestRepsSearch) {
      this.rbs.getRepCodes(value || '%').subscribe(res => {
        if (res.names && res.names.length > 0) {
          this.repUnits = res.names;
        } else if (!res?.names || res.names.length === 0) {
          this.repUnits = [];
        }
      });
    } else {
      this.repUnits = this.dropdownRepCodes.filter(
        rep =>
          rep.repName.toLowerCase().includes(value.toLowerCase()) ||
          rep.repCode.toLowerCase().includes(value.toLowerCase())
      );
      if (this.repUnits.length === 0) {
        this.repUnits = [];
      }
    }
  }

  onBlur(ev) {
    if (ev.target.value === '') {
      this.repCodeControl.setValue(this.previousSelectedVal);
    }
  }

  changeSelection(ev) {
    this.previousSelectedVal = ev.option.value;
    this.selectionChange.emit(ev);
  }

  resetFilter(ev) {
    if (this.repCodeControl['value']) {
      this.previousSelectedVal = '';
      this.repCodeControl.setValue('');
    }
    this.clearFilter.emit(ev);
  }

  getRepCodeList(searchString?: string) {
    this.usServ.repCodeSearch(searchString).subscribe(resp => {
      this.dropdownNoResults = true;
      const newRepCodeList = resp.names.map(rep => ({
        value: rep.repCode,
        display: rep.name,
      }));
      this.field.fullField.externalFilter['options'] = newRepCodeList;
      if (this.field.fullField.externalFilter['options'].length > 0) {
        this.dropdownNoResults = false;
      }
      this.dropdownLoading = false;
    });
  }

  showClearButton(fieldName) {
    if (
      this.filterForm.controls[this.groupKey].controls[fieldName].value ||
      this.previousSelectedVal
    ) {
      return true;
    }
    return false;
  }
}
